import {useMemo} from 'react';
import {Stack, useTheme} from '@mui/material';
import type {ByCategoryResultPage, ThesaurusId} from 'services/thesaurus/types';
import {useLocalization} from 'localization';
import {Card, FlexBox, Layout, Spacer, Text} from 'components';
import {ThesaurusCode} from 'feature/editThesaurus/components';
import {useThesaurusContext} from '../hooks';
import {SearchResultMatch} from './SearchResultMatch';

type Props = {
  thesaurusId: ThesaurusId;
  resultByCategories: ByCategoryResultPage[];
  Pager: React.FC;
};

export const SearchResultPage: React.FC<Props> = ({
  thesaurusId,
  resultByCategories,
  Pager,
}) => {
  const {t} = useLocalization();
  const {colors} = useTheme();
  const {getDisplayCode} = useThesaurusContext(thesaurusId);
  const {allMatches} = useMemo(() => {
    return {
      allMatches: resultByCategories.map(r => r.nodes).flat(),
    };
  }, [resultByCategories]);

  return resultByCategories.length === 0 ? (
    <Text kind="primary" variant="body2">
      {t('extension.thesaurusEdit.search.noMatches')}
    </Text>
  ) : (
    <Stack
      sx={{
        flex: 1,
      }}>
      <Card colorx={colors.offWhite} spacing={1}>
        <Card.Header>
          {/* Label of cateogires visible on this page */}
          <Layout
            flex={1}
            horizontal
            adjustCenter
            justifyContent={'space-between'}>
            <Layout horizontal adjustCenter>
              {resultByCategories.map(match => {
                const code = getDisplayCode(match.categoryNode);
                return (
                  <FlexBox horizontal center key={match.categoryNode.id}>
                    {code ? <ThesaurusCode code={code} /> : null}
                    {code ? <Spacer size={1} /> : null}
                    <Text kind="primary" variant="h3">
                      {match.categoryNode.label}
                    </Text>
                    <Spacer size={2} />
                  </FlexBox>
                );
              })}
            </Layout>
            {/* Pager, provided by parent compoent */}
            <Pager />
          </Layout>
        </Card.Header>

        {/* Items on this page */}
        <Card.Content>
          <Stack id={'searchMatchStack'} spacing={1}>
            {allMatches.map(node => (
              <SearchResultMatch
                key={node.node.id}
                thesaurusId={thesaurusId}
                match={node}
              />
            ))}
          </Stack>
        </Card.Content>
      </Card>
    </Stack>
  );
};
